// @workflow platform.viewer
/**
 * Linked views: the 3D camera stands at the open 360's position and follows it
 * (`store.state.areViewsLocked`). While linked, the main extension's camera handler puts the
 * camera back at the 360 position after any other move (mainExtension.js), so framing elements
 * in 3D is undone. The viewer's Link and Unlink buttons (pageView.vue) and the assistant's tools
 * run these same operations (AVA-744), so the two cannot drift.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
// Read only when an operation runs, so the import cycle with core.js is harmless.
import { PANE_TYPE_3D } from './core.js';

/**
 * A captured 3D focal length at or below this is a widened or garbage value (taken while the 360
 * link had stretched the FOV to the panorama HFOV), not a usable focal length.
 */
export const MIN_VALID_FOCAL_LENGTH = 10;

/**
 * Last-resort 3D vertical FOV (degrees), used only when the model exposes no perspective default
 * camera to read the real FOV from. About 45° is the conventional viewer default.
 */
export const DEFAULT_3D_FOV_DEGREES = 45;

/** Forge's first-person walk tool, which the link turns on for the 360 look. */
const BIM_WALK_EXTENSION = 'Autodesk.BimWalk';

/** @param {object} ctx see core.js `createViewer` */
export function createLinkedViewOps(ctx) {
  const { store, deps, current, service } = ctx;

  /**
   * Restore a normal 3D FOV: the 360 link widens the camera FOV to the panorama HFOV, and
   * `initFocalLength` (captured at OBJECT_TREE_CREATED) is a garbage telephoto value when it was
   * captured while the FOV was already widened (the 3D loaded in the background during a 360
   * measurement). Prefer the captured focal length when it is sane; otherwise the model's own
   * authored default FOV (getDefaultCamera, immune to the runtime widening), and only then
   * DEFAULT_3D_FOV_DEGREES. (The old `fl <= 10 ? 200 : fl` fallback set focal 200 ≈ 6.9°
   * telephoto, which magnified the interior view into the "surfaces not smooth" artifact.)
   */
  function restoreNormal3dFov(viewer = current()) {
    if (!viewer) return;
    const mainExtension = service() && service().getMainExtension();
    const focalLength = mainExtension ? mainExtension.initFocalLength : undefined;
    if (focalLength > MIN_VALID_FOCAL_LENGTH) {
      viewer.setFocalLength(focalLength);
      return;
    }
    const defaultCamera = viewer.model && viewer.model.getDefaultCamera ? viewer.model.getDefaultCamera() : null;
    const modelDefaultFov = defaultCamera ? defaultCamera.fov : undefined;
    viewer.setFOV(modelDefaultFov > 0 ? modelDefaultFov : DEFAULT_3D_FOV_DEGREES);
  }

  return {
    /** The 3D view is linked to the open 360. */
    isLinkedTo360: () => !!store.state.areViewsLocked,
    /**
     * The Link button's rule (pageView `canLinkViews`): not linked yet, the 3D view visible and a
     * 360 open.
     */
    canLinkTo360() {
      if (store.state.areViewsLocked) return false;
      const mainApp = store.state.mainApp;
      if (!mainApp) return false;
      const index = mainApp.getViewTypeIndex(PANE_TYPE_3D);
      return !!(mainApp[`isView${index}Visible`] && store.state.selectedImage);
    },
    /**
     * The Unlink button: the camera is free again. BimWalk is closed, the navigation lock is
     * lifted (forgeviewer TurnViewerLockOff), the 360 markers are hidden and the normal FOV is back.
     * The flag is cleared again at the end, as the button always did.
     */
    unlinkFrom360() {
      store.state.areViewsLocked = false;
      const viewer = current();
      if (!viewer) return;
      const bimWalk = viewer.getExtension(BIM_WALK_EXTENSION);
      if (bimWalk) bimWalk.deactivate();
      deps.unlockNavigation();
      (store.state.projects || []).forEach((project) => (project.images || []).forEach((image) => {
        image.isShown = false;
      }));
      restoreNormal3dFov(viewer);
      store.state.areViewsLocked = false;
    },
    /**
     * The Link button: forgeService.linkViewers puts the camera at the 360 position and links the
     * views. It returns silently without a 360, a viewer, or while a crop session keeps the 360,
     * so the answer is whether the views are linked afterwards.
     * @param {{ force?: boolean }} [options] `force` re-links even when the flag is already set
     * @returns {boolean}
     */
    linkTo360({ force } = {}) {
      service().linkViewers(force);
      return !!store.state.areViewsLocked;
    },
    restoreNormal3dFov,
  };
}
